// src/components/BulkIDCardGenerator.js (Updated with dual CSV upload options)
import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ReactDOM from 'react-dom/client';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  LinearProgress,
  Alert,
  Chip,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  FormGroup,
  Switch,
  Card,
  CardContent,
  InputAdornment,
  ButtonGroup
} from '@mui/material';
import {
  Download,
  Users,
  CreditCard,
  CheckCircle,
  X,  
  Search,
  Filter,
  RefreshCw,
  Eye,
  EyeOff,
  Settings,
  Palette,
  FileText,
  Upload,
  Trash2,
  RotateCcw,
  Building
} from 'lucide-react';
import adminService from './AdminService';
import { generateQRCodeSVG } from './QRCodeGenerator';
import CSVUploadIDGenerator from './CSVUploadIDGenerator';
import MultiDomainCSVUploader from './MultiDomainCSVUploader';
import ImageBatchBase64Converter from './ImageBatchBase64Converter';

// Import color schemes and design patterns from ProfilePageIDCard
const colorSchemes = {
  blue: {
    name: 'Corporate Blue',
    frontBg: '#3b82f6',
    frontText: 'white',
    frontHighlight: 'rgba(255,255,255,0.2)',
    frontAccent: 'rgba(255,255,255,0.8)',
    backBg: '#1e293b',
    backText: 'white',
    backHighlight: 'rgba(255,255,255,0.2)',
    backAccent: 'rgba(255,255,255,0.8)',
    primary: '#3b82f6',
    secondary: '#1e293b',
    accent: '#60a5fa',
    cardBorder: '2px solid #e2e8f0'
  },
  white: {
    name: 'Minimal White',
    frontBg: '#f8fafc',
    frontText: '#1e293b',
    frontHighlight: '#e2e8f0',
    frontAccent: '#94a3b8',
    backBg: '#ffffff',
    backText: '#0f172a',
    backHighlight: '#f1f5f9',
    backAccent: '#cbd5e1',
    primary: '#0f172a',
    secondary: '#475569',
    accent: '#94a3b8',
    cardBorder: '2px solid #e2e8f0'
  },
  teal: {
    name: 'Modern Teal',
    frontBg: '#14b8a6',
    frontText: 'white',
    frontHighlight: 'rgba(255,255,255,0.2)',
    frontAccent: 'rgba(255,255,255,0.8)',
    backBg: '#0f766e',
    backText: 'white',
    backHighlight: 'rgba(255,255,255,0.2)',
    backAccent: 'rgba(255,255,255,0.8)',
    primary: '#14b8a6',
    secondary: '#0f766e',
    accent: '#5eead4',
    cardBorder: '2px solid #e2e8f0'
  },
  purple: {
    name: 'Creative Purple',
    frontBg: '#9333ea',
    frontText: 'white',
    frontHighlight: 'rgba(255,255,255,0.2)',
    frontAccent: 'rgba(255,255,255,0.8)',
    backBg: '#581c87',
    backText: 'white',
    backHighlight: 'rgba(255,255,255,0.2)',
    backAccent: 'rgba(255,255,255,0.8)',
    primary: '#9333ea',
    secondary: '#581c87',
    accent: '#c084fc',
    cardBorder: '2px solid #e2e8f0'
  }
};

const designPatterns = {
  none: { name: "None" },
  dots: { name: "Dots" },
  lines: { name: "Lines" },
  grid: { name: "Grid" }
};

// TabPanel Component
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// VCard generation function
const generateVCard = (userData) => {
  const nameParts = userData.name.split(' ');
  const lastName = nameParts.length > 1 ? nameParts.pop() : '';
  const firstName = nameParts.join(' ');

  let vcard = 'BEGIN:VCARD\nVERSION:3.0\n';
  vcard += `FN:${userData.name}\n`;
  vcard += `N:${lastName};${firstName};;;\n`;
  vcard += `TITLE:${userData.title || ''}\n`;
  vcard += `ORG:${userData.company || ''}\n`;
  vcard += `EMAIL;type=WORK:${userData.email || ''}\n`;
  vcard += `TEL;type=CELL:${userData.phone || ''}\n`;
  vcard += `ADR;type=WORK:;;${userData.location || ''}\n`;
  vcard += `NOTE:Employee ID: ${userData.employeeId || ''}\n`;
  vcard += 'END:VCARD';
  return vcard;
};

// Enhanced Local Storage functions with metadata-only storage
const LOCAL_STORAGE_KEY = 'admin_id_card_exports';
const MAX_ENTRIES = 50; // Can store more since we're only storing metadata

// Check localStorage size
const getLocalStorageSize = () => {
  let total = 0;
  for (let key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      total += localStorage[key].length + key.length;
    }
  }
  return total;
};

// Clean up old entries
const cleanupOldEntries = () => {
  try {
    const existing = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '[]');
    if (existing.length > MAX_ENTRIES) {
      const cleaned = existing.slice(0, MAX_ENTRIES);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cleaned));
      console.log(`Cleaned up ${existing.length - MAX_ENTRIES} old entries`);
    }
  } catch (error) {
    console.warn('Error during cleanup:', error);
  }
};

// Save metadata only (approach 2)
const saveExportMetadataOnly = (user, pdfBlob, filename, cardSettings, editableValues) => {
  try {
    cleanupOldEntries();

    const exportMeta = {
      id: `${user.id}_${Date.now()}`,
      user_id: user.id,
      user_name: user.name,
      user_email: user.email,
      filename,
      file_size: pdfBlob.size,
      created_at: new Date().toISOString(),
      // Store all data needed to regenerate PDF
      user_data: {
        ...user,
        employeeId: editableValues.employeeIds[user.id] !== undefined ? editableValues.employeeIds[user.id] : user.employeeId,
        department: editableValues.departments[user.id] !== undefined ? editableValues.departments[user.id] : user.department,
        bloodGroup: editableValues.bloodGroups[user.id] !== undefined ? editableValues.bloodGroups[user.id] : (user.bloodGroup || 'Not Specified'),
        designation: editableValues.designations[user.id] !== undefined ? editableValues.designations[user.id] : (user.designation || user.title || 'Employee')
      },
      card_settings: cardSettings,
      storage_type: 'metadata_only'
    };

    let existing = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '[]');
    existing.unshift(exportMeta);
    
    if (existing.length > MAX_ENTRIES) {
      existing = existing.slice(0, MAX_ENTRIES);
    }
    
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(existing));
    console.log('Export metadata saved successfully');
    
    return exportMeta;
  } catch (error) {
    console.error('Error saving metadata:', error);
    throw new Error('Failed to save export metadata');
  }
};

// Get exports from localStorage
const getExportsFromLocal = () => {
  try {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error reading exports from localStorage:', error);
    return [];
  }
};

// Clear all stored exports
const clearAllStoredExports = () => {
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    console.log('All stored exports cleared successfully');
  } catch (error) {
    console.error('Error clearing stored exports:', error);
  }
};

// Regenerate PDF from metadata
const regeneratePDFFromMetadata = async (exportMeta, renderCardFunction) => {
  try {
    const { user_data, card_settings } = exportMeta;
    
    // Create containers for rendering
    const [frontContainer, backContainer] = ['front', 'back'].map(() => {
      const container = document.createElement('div');
      container.style.width = '380px';
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      document.body.appendChild(container);
      return container;
    });

    // Render both sides
    ReactDOM.createRoot(frontContainer).render(
      <div style={{ width: '380px', height: '608px' }}>
        {renderCardFunction(user_data, 'front', card_settings)}
      </div>
    );
    ReactDOM.createRoot(backContainer).render(
      <div style={{ width: '380px', height: '608px' }}>
        {renderCardFunction(user_data, 'back', card_settings)}
      </div>
    );

    // Wait for rendering
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const cardWidth = 54;
    const cardHeight = 85.6;
    const margin = 15;
    const spacing = 10;

    // Canvas options for high quality rendering
    const canvasOptions = {
      scale: 4,
      useCORS: true,
      allowTaint: true,
      backgroundColor: null,
      width: 380,
      height: 608,
      logging: false,
      onclone: (clonedDoc) => {
        const style = clonedDoc.createElement('style');
        style.textContent = `
          * { 
            -webkit-font-smoothing: antialiased !important;
            font-family: Arial, sans-serif !important;
          }
          img { 
            image-rendering: -webkit-optimize-contrast !important; 
          }
          svg {
            shape-rendering: geometricPrecision !important;
          }
        `;
        clonedDoc.head.appendChild(style);
      }
    };

    // Convert to canvas
    const [frontCanvas, backCanvas] = await Promise.all([
      html2canvas(frontContainer.firstChild, canvasOptions),
      html2canvas(backContainer.firstChild, canvasOptions)
    ]);

    // Add to PDF
    pdf.addImage(
      frontCanvas.toDataURL('image/jpeg', 1.0),
      'JPEG',
      margin,
      margin,
      cardWidth,
      cardHeight,
      undefined,
      'FAST'
    );
    pdf.addImage(
      backCanvas.toDataURL('image/jpeg', 1.0),
      'JPEG',
      margin + cardWidth + spacing,
      margin,
      cardWidth,
      cardHeight,
      undefined,
      'FAST'
    );

    // Cleanup
    [frontContainer, backContainer].forEach(container => {
      document.body.removeChild(container);
    });

    return pdf;
  } catch (error) {
    console.error('Error regenerating PDF:', error);
    throw new Error('Failed to regenerate PDF');
  }
};

const BulkIDCardGenerator = React.memo(({ open, onClose, onExportComplete }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [error, setError] = useState('');
  const [generationStatus, setGenerationStatus] = useState('');
  const [previewVisible, setPreviewVisible] = useState({});
  const [activeSettingsTab, setActiveSettingsTab] = useState(0);
  const [editableEmployeeIds, setEditableEmployeeIds] = useState({});
  const [editableDepartments, setEditableDepartments] = useState({});
  const [editableBloodGroups, setEditableBloodGroups] = useState({});
  const [editableDesignations, setEditableDesignations] = useState({});
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [showMultiDomainCSV, setShowMultiDomainCSV] = useState(false); // New state for multi-domain CSV
  const [idCardExports, setIdCardExports] = useState([]);
  const [regenerating, setRegenerating] = useState({});

  // Central ID Card Settings
  const [cardSettings, setCardSettings] = useState({
    colorScheme: 'blue',
    designPattern: 'none',
    includePhoto: true,
    includeID: true,
    includePosition: true,
    includeDepartment: true,
    includeCompany: true,
    includeIssueDates: true,
    includeQR: true,
    includeContactInfo: true,
    includeEmergencyInfo: true,
    includeBloodGroupFront: true,
    includeBloodGroupBack: true,
    qrCodeType: 'vcard',
    showBorder: true,
    roundedCorners: true,
    showShadow: true,
    emergencyContact: {
      name: "",
      relation: "",
      phone: "",
      email: ""
    }
  });

  // Refs for card generation
  const cardContainerRef = useRef(null);

  // Helper functions for getting editable values
  const getEmployeeId = (user) => {
    return editableEmployeeIds[user.id] !== undefined 
      ? editableEmployeeIds[user.id] 
      : user.employeeId;
  };

  const getDepartment = (user) => {
    return editableDepartments[user.id] !== undefined 
      ? editableDepartments[user.id] 
      : user.department;
  };

  const getBloodGroup = (user) => {
    return editableBloodGroups[user.id] !== undefined 
      ? editableBloodGroups[user.id] 
      : (user.bloodGroup || 'Not Specified');
  };

  const getDesignation = (user) => {
    return editableDesignations[user.id] !== undefined 
      ? editableDesignations[user.id] 
      : (user.designation || user.title || 'Employee');
  };

  // Get current user data from localStorage
  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      return null;
    }
  };

  // Extract domain from email
  const extractDomain = (email) => {
    if (!email) return '';
    return email.split('@')[1]?.toLowerCase() || '';
  };

  // Filter users based on current user's role and domain
  const filterUsersByDomain = (usersList) => {
    const currentUser = getCurrentUser();
    
    if (!currentUser) {
      console.warn('No current user found in localStorage');
      return [];
    }

    // If user role is 'user', show nothing
    if (currentUser.role === 'user') {
      return [];
    }

    // If user role is 'superadmin', show all users
    if (currentUser.role === 'superadmin') {
      return usersList;
    }

    // If user role is 'admin', show only users from same domain
    if (currentUser.role === 'admin') {
      const currentUserDomain = extractDomain(currentUser.email);
      if (!currentUserDomain) {
        console.warn('Could not extract domain from current user email');
        return [];
      }

      return usersList.filter(user => {
        const userDomain = extractDomain(user.email);
        return userDomain === currentUserDomain;
      });
    }

    return [];
  };

  // Fetch users when component opens
  useEffect(() => {
    if (open) {
      fetchUsers();
      loadIdCardExports();
    }
  }, [open]);

  // Auto-select all filtered users initially
  useEffect(() => {
    if (filteredUsers.length > 0) {
      setSelectedUsers(new Set(filteredUsers.map(user => user.id)));
    }
  }, [filteredUsers]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminService.getAllUsers();
      const formattedUsers = data.map(user => ({
        ...user,
        name: user.name || 'No Name Set',
        company: user.company || 'Company Name',
        title: user.title || 'Employee',
        designation: user.designation || user.title || 'Employee',
        department: user.department || 'General',
        email: user.email || '',
        phone: user.phone || '+1 (555) 123-4567',
        location: user.location || 'Office Location',
        avatar: user.avatar || '/api/placeholder/150/150',
        employeeId: user.employeeId || `EMP-${String(user.id).padStart(4, '0')}`,
        bloodGroup: user.bloodGroup || 'Not Specified'
      }));
      
      setUsers(formattedUsers);
      
      // Filter users based on current user's domain and role
      const domainFilteredUsers = filterUsersByDomain(formattedUsers);
      setFilteredUsers(domainFilteredUsers);
      
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  // Apply additional filters (search, department) on domain-filtered users
  const getDisplayUsers = () => {
    return filteredUsers.filter(user => {
      const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           user.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDepartment = filterDepartment === 'all' || user.department === filterDepartment;
      return matchesSearch && matchesDepartment;
    });
  };

  const displayUsers = getDisplayUsers();

  // Handle user selection
  const handleUserToggle = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  // Handle select all / deselect all
  const handleSelectAll = () => {
    if (selectedUsers.size === displayUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(displayUsers.map(user => user.id)));
    }
  };

  // Get unique departments
  const departments = [...new Set(filteredUsers.map(user => user.department))];

  // Handle settings change
  const handleSettingsChange = (key, value) => {
    setCardSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle emergency contact change
  const handleEmergencyContactChange = (field, value) => {
    setCardSettings(prev => ({
      ...prev,
      emergencyContact: {
        ...prev.emergencyContact,
        [field]: value
      }
    }));
  };

  // Handle editing functions
  const handleEmployeeIdChange = (userId, newValue) => {
    setEditableEmployeeIds(prev => ({
      ...prev,
      [userId]: newValue
    }));
  };

  const handleDepartmentChange = (userId, newValue) => {
    setEditableDepartments(prev => ({
      ...prev,
      [userId]: newValue
    }));
  };

  const handleBloodGroupChange = (userId, newValue) => {
    setEditableBloodGroups(prev => ({
      ...prev,
      [userId]: newValue
    }));
  };

  const handleDesignationChange = (userId, newValue) => {
    setEditableDesignations(prev => ({
      ...prev,
      [userId]: newValue
    }));
  };

  // Handle CSV data import (works for both standard and multi-domain)
  const handleCSVData = (data) => {
    try {
      console.log('Received CSV data:', data);
      
      // Format the imported data to match your user structure
      const formattedUsers = data.map((user, index) => ({
        ...user,
        id: user.id || `csv_${Date.now()}_${index}`, // Ensure unique IDs for CSV imports
        name: user.name || 'No Name Set',
        email: user.email || '',
        phone: user.phone || '+1 (555) 123-4567',
        company: user.company || 'Company Name',
        department: user.department || 'General',
        designation: user.designation || user.title || 'Employee',
        employeeId: user.employeeId || `CSV-${String(index + 1).padStart(4, '0')}`,
        bloodGroup: user.bloodGroup || 'Not Specified',
        location: user.location || 'Office Location',
        avatar: user.avatar || '/api/placeholder/150/150',
        title: user.title || user.designation || 'Employee',
        isCSVImport: true // Flag to identify CSV imports
      }));

      // Update users state with imported data
      setUsers(formattedUsers);
      setFilteredUsers(formattedUsers); // For CSV imports, don't apply domain filtering
      
      // Auto-select all imported users
      setSelectedUsers(new Set(formattedUsers.map(user => user.id)));
      
      // Close any open CSV dialogs
      setShowCSVUpload(false);
      setShowMultiDomainCSV(false);

      console.log('Processed CSV users with photos:', formattedUsers.map(u => ({
        name: u.name,
        hasPhoto: !!u.avatar && u.avatar !== '/api/placeholder/150/150',
        domain: u.email?.split('@')[1]
      })));

    } catch (error) {
      console.error('Error processing CSV data:', error);
      setError('Failed to process CSV data. Please check the format and try again.');
    }
  };

  // Render card component
  const renderCard = (user, side, settings = cardSettings) => {
    const colors = colorSchemes[settings.colorScheme];
    const employeeId = getEmployeeId(user);
    const department = getDepartment(user);
    const designation = getDesignation(user);
    const bloodGroup = getBloodGroup(user);

    return (
      <div style={{
        width: 380,
        height: 608,
        padding: 24,
        backgroundColor: side === 'front' ? colors.frontBg : colors.backBg,
        color: side === 'front' ? colors.frontText : colors.backText,
        borderRadius: settings.roundedCorners ? 16 : 0,
        boxShadow: settings.showShadow ? '0 8px 16px rgba(0,0,0,0.15)' : 'none',
        border: settings.showBorder ? colors.cardBorder : 'none',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'Arial, sans-serif'
      }}>
        {side === 'front' ? (
          // Front side content
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {settings.includeCompany && (
              <h3 style={{ margin: '0 0 16px', fontSize: 18, color: colors.frontText }}>
                {user.company}
              </h3>
            )}
            
            <div style={{ textAlign: 'center', flex: 1 }}>
              {settings.includePhoto && (
                <div style={{
                  width: 160,
                  height: 160,
                  margin: '0 auto 16px',
                  borderRadius: '50%',
                  border: `4px solid ${colors.frontAccent}`,
                  overflow: 'hidden',
                  backgroundColor: colors.frontHighlight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {user.avatar && user.avatar !== '/api/placeholder/150/150' ? (
                    <img 
                      src={user.avatar} 
                      alt="Profile" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = 'Photo'; }}
                    />
                  ) : 'Photo'}
                </div>
              )}
              
              <h2 style={{ margin: '0 0 8px', fontSize: 24, color: colors.frontText }}>{user.name}</h2>
              {settings.includePosition && (
                <p style={{ margin: '0 0 4px', fontSize: 16, color: colors.frontText }}>{designation}</p>
              )}
              {settings.includeDepartment && (
                <p style={{ margin: '0 0 4px', fontSize: 14, color: colors.frontText }}>{department}</p>
              )}
            </div>

            {settings.includeID && (
              <div style={{
                backgroundColor: colors.frontHighlight,
                padding: 16,
                borderRadius: 8,
                marginTop: 'auto'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>Employee ID:</span>
                  <span style={{ fontWeight: 'bold' }}>{employeeId}</span>
                </div>
                {settings.includeBloodGroupFront && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Blood Group:</span>
                    <span style={{ fontWeight: 'bold' }}>{bloodGroup}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          // Back side content
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {settings.includeContactInfo && (
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ margin: '0 0 12px', color: colors.accent }}>CONTACT INFORMATION</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <p style={{ margin: 0 }}>📧 {user.email}</p>
                  <p style={{ margin: 0 }}>📞 {user.phone}</p>
                  <p style={{ margin: 0 }}>📍 {user.location}</p>
                </div>
              </div>
            )}

            {settings.includeQR && (
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <h4 style={{ margin: '0 0 12px', color: colors.accent }}>SCAN FOR CONTACT INFO</h4>
                <div style={{ 
                  backgroundColor: 'white', 
                  padding: 8, 
                  borderRadius: 4,
                  display: 'inline-block'
                }}>
                  <QRCodeSVG
                    value={generateVCard(user)}
                    size={120}
                    level="M"
                    includeMargin={false}
                  />
                </div>
              </div>
            )}

            {settings.includeBloodGroupBack && (
              <div style={{ marginTop: 'auto', textAlign: 'center' }}>
                <p style={{ margin: '0 0 8px', fontSize: 14 }}>
                  Blood Group: <strong>{bloodGroup}</strong>
                </p>
              </div>
            )}

            {settings.includeEmergencyInfo && (
              <div style={{ marginTop: 'auto', marginBottom: 16 }}>
                <h4 style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#ff6b6b', fontSize: 14 }}>
                  IN CASE OF EMERGENCY
                </h4>
                {settings.emergencyContact.name || settings.emergencyContact.phone ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {settings.emergencyContact.name && (
                      <p style={{ margin: 0, fontSize: 14, color: colors.backText }}>
                        👤 {settings.emergencyContact.name}
                        {settings.emergencyContact.relation && ` (${settings.emergencyContact.relation})`}
                      </p>
                    )}
                    {settings.emergencyContact.phone && (
                      <p style={{ margin: 0, fontSize: 14, color: colors.backText }}>
                        📞 {settings.emergencyContact.phone}
                      </p>
                    )}
                    {settings.emergencyContact.email && (
                      <p style={{ margin: 0, fontSize: 14, color: colors.backText }}>
                        📧 {settings.emergencyContact.email}
                      </p>
                    )}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: 14, color: colors.backText }}>
                    Please contact HR Department: hr@{user.company.toLowerCase().replace(/\s+/g, '')}.com
                  </p>
                )}
              </div>
            )}

            {settings.includeIssueDates && (
              <div style={{
                borderTop: `1px solid ${colors.backHighlight}`,
                paddingTop: 8,
                marginTop: 8
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: colors.backText }}>
                  <span>Issue Date: {new Date().toLocaleDateString()}</span>
                  <span>Valid Until: {new Date(new Date().setFullYear(new Date().getFullYear() + 3)).toLocaleDateString()}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Generate PDFs for selected users with metadata storage
  const handleGeneratePDFs = async () => {
    try {
      setGenerating(true);
      setProgress(0);

      const selectedUsersList = displayUsers.filter(user => selectedUsers.has(user.id));
      
      // Create PDF document
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      // Card dimensions and settings
      const cardWidth = 54;
      const cardHeight = 85.6;
      const margin = 15;
      const spacing = 10;

      // Current editable values for regeneration
      const editableValues = {
        employeeIds: editableEmployeeIds,
        departments: editableDepartments,
        bloodGroups: editableBloodGroups,
        designations: editableDesignations
      };

      // Process each user
      for (let i = 0; i < selectedUsersList.length; i++) {
        const user = selectedUsersList[i];
        if (i > 0) pdf.addPage();

        const [frontContainer, backContainer] = ['front', 'back'].map(() => {
          const container = document.createElement('div');
          container.style.width = '380px';
          container.style.position = 'absolute';
          container.style.left = '-9999px';
          document.body.appendChild(container);
          return container;
        });

        // Render both sides
        ReactDOM.createRoot(frontContainer).render(
          <div style={{ width: '380px', height: '608px' }}>{renderCard(user, 'front')}</div>
        );
        ReactDOM.createRoot(backContainer).render(
          <div style={{ width: '380px', height: '608px' }}>{renderCard(user, 'back')}</div>
        );

        // Wait for rendering
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Canvas options for high quality rendering
        const canvasOptions = {
          scale: 4,
          useCORS: true,
          allowTaint: true,
          backgroundColor: null,
          width: 380,
          height: 608,
          logging: false,
          onclone: (clonedDoc) => {
            const style = clonedDoc.createElement('style');
            style.textContent = `
              * { 
                -webkit-font-smoothing: antialiased !important;
                font-family: Arial, sans-serif !important;
              }
              img { 
                image-rendering: -webkit-optimize-contrast !important; 
              }
              svg {
                shape-rendering: geometricPrecision !important;
              }
            `;
            clonedDoc.head.appendChild(style);
          }
        };

        // Convert to canvas
        const [frontCanvas, backCanvas] = await Promise.all([
          html2canvas(frontContainer.firstChild, canvasOptions),
          html2canvas(backContainer.firstChild, canvasOptions)
        ]);

        // Add to PDF
        pdf.addImage(
          frontCanvas.toDataURL('image/jpeg', 1.0),
          'JPEG',
          margin,
          margin,
          cardWidth,
          cardHeight,
          undefined,
          'FAST'
        );
        pdf.addImage(
          backCanvas.toDataURL('image/jpeg', 1.0),
          'JPEG',
          margin + cardWidth + spacing,
          margin,
          cardWidth,
          cardHeight,
          undefined,
          'FAST'
        );

        // Cleanup
        [frontContainer, backContainer].forEach(container => {
          document.body.removeChild(container);
        });

        setProgress((i + 1) / selectedUsersList.length * 100);

        // Save metadata only (not the full PDF)
        const pdfBlob = pdf.output('blob');
        const filename = `ID_Card_${getEmployeeId(user)}_${new Date().toISOString().split('T')[0]}.pdf`;
        
        try {
          saveExportMetadataOnly(user, pdfBlob, filename, cardSettings, editableValues);
        } catch (error) {
          console.warn(`Failed to save metadata for ${user.name}:`, error);
        }
      }

      // Save PDF
      pdf.save(`ID_Cards_${new Date().toISOString().split('T')[0]}.pdf`);
      
      setGenerating(false);
      loadIdCardExports();
      onExportComplete?.();

    } catch (error) {
      console.error('Error generating PDFs:', error);
      setGenerating(false);
      setError('Failed to generate PDF. Please try again.');
    }
  };

  const togglePreview = (userId) => {
    setPreviewVisible(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Load ID card exports from local storage
  const loadIdCardExports = () => {
    try {
      const savedExports = getExportsFromLocal();
      setIdCardExports(savedExports);
    } catch (error) {
      console.error('Error loading ID card exports:', error);
    }
  };

  // Regenerate and download PDF from metadata
  const handleRegenerateAndDownload = async (exportMeta) => {
    try {
      setRegenerating(prev => ({ ...prev, [exportMeta.id]: true }));
      
      const pdf = await regeneratePDFFromMetadata(exportMeta, renderCard);
      pdf.save(exportMeta.filename);
      
    } catch (error) {
      console.error('Error regenerating PDF:', error);
      setError('Failed to regenerate PDF. Please try again.');
    } finally {
      setRegenerating(prev => ({ ...prev, [exportMeta.id]: false }));
    }
  };

  // Delete export metadata
  const handleDeleteExport = (exportId) => {
    try {
      const existing = getExportsFromLocal();
      const filtered = existing.filter(exp => exp.id !== exportId);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(filtered));
      setIdCardExports(filtered);
    } catch (error) {
      console.error('Error deleting export:', error);
    }
  };

  // Clear all exports
  const handleClearAllExports = () => {
    clearAllStoredExports();
    setIdCardExports([]);
  };

  // Show access denied message if user doesn't have permission
  if (open && filteredUsers.length === 0 && !loading && !users.some(u => u.isCSVImport)) {
    const currentUser = getCurrentUser();
    if (!currentUser || currentUser.role === 'user') {
      return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CreditCard size={24} />
              <Typography variant="h6">Access Denied</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body1">
                You don't have permission to generate ID cards. Only administrators can access this feature.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </Dialog>
      );
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CreditCard size={24} />
          <Typography variant="h6">Bulk ID Card Generator</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1">
            Select users to generate ID cards. You can also upload a CSV file to import users.
          </Typography>
          
          {/* Enhanced CSV Upload Options */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <ButtonGroup variant="outlined">
              <Button
                startIcon={<Upload />}
                onClick={() => setShowCSVUpload(true)}
              >
                Import from CSV
              </Button>
              <Button
                startIcon={<Building />}
                onClick={() => setShowMultiDomainCSV(true)}
                sx={{ 
                  borderLeft: '1px solid rgba(25, 118, 210, 0.5) !important',
                  position: 'relative'
                }}
              >
                Multi-Domain CSV *
                <Typography
                  component="sup"
                  sx={{
                    fontSize: '10px',
                    color: 'primary.main',
                    position: 'absolute',
                    top: 2,
                    right: 2
                  }}
                >
                  NEW
                </Typography>
              </Button>
            </ButtonGroup>
            
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<Trash2 />}
              onClick={handleClearAllExports}
              disabled={idCardExports.length === 0}
            >
              Clear All Exports
            </Button>
          </Box>
          
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            * Multi-Domain CSV allows importing users from multiple email domains (e.g., company.com, subsidiary.com)
            <br />
            Storage: {(getLocalStorageSize() / 1024).toFixed(1)} KB used (metadata only)
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Search by name or email"
              variant="outlined"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                label="Department"
              >
                <MenuItem value="all">All Departments</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            onClick={handleSelectAll}
            startIcon={selectedUsers.size === displayUsers.length ? <CheckCircle /> : <X />}
            color={selectedUsers.size === displayUsers.length ? "success" : "primary"}
            size="large"
            fullWidth
          >
            {selectedUsers.size === displayUsers.length ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>

        {generating && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" color="text.secondary">
              Generating PDFs... {Math.round(progress)}%
            </Typography>
          </Box>
        )}

        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedUsers.size === displayUsers.length && displayUsers.length > 0}
                    indeterminate={selectedUsers.size > 0 && selectedUsers.size < displayUsers.length}
                    onChange={handleSelectAll}
                    color="primary"
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayUsers.map((user) => (
                <React.Fragment key={user.id}>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedUsers.has(user.id)}
                        onChange={() => handleUserToggle(user.id)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar src={user.avatar} alt={user.name} sx={{ width: 32, height: 32 }} />
                        <Typography variant="body1" noWrap>{user.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell>{getDepartment(user)}</TableCell>
                    <TableCell>
                      {user.isCSVImport ? (
                        <Chip size="small" label="CSV Import" color="info" variant="outlined" />
                      ) : (
                        <Chip size="small" label="Database" color="default" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => togglePreview(user.id)}
                        size="small"
                        color="primary"
                      >
                        {previewVisible[user.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {previewVisible[user.id] && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Box sx={{ p: 2, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Box sx={{ transform: 'scale(0.3)', transformOrigin: 'top left' }}>
                            {renderCard(user, 'front')}
                          </Box>
                          <Box sx={{ transform: 'scale(0.3)', transformOrigin: 'top left' }}>
                            {renderCard(user, 'back')}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography variant="h6" gutterBottom>
            ID Card Settings
          </Typography>
          <Card variant="outlined">
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Color Scheme</InputLabel>
                    <Select
                      value={cardSettings.colorScheme}
                      onChange={(e) => handleSettingsChange('colorScheme', e.target.value)}
                      label="Color Scheme"
                    >
                      {Object.keys(colorSchemes).map((key) => (
                        <MenuItem key={key} value={key}>
                          {colorSchemes[key].name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Design Pattern</InputLabel>
                    <Select
                      value={cardSettings.designPattern}
                      onChange={(e) => handleSettingsChange('designPattern', e.target.value)}
                      label="Design Pattern"
                    >
                      {Object.keys(designPatterns).map((key) => (
                        <MenuItem key={key} value={key}>
                          {designPatterns[key].name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cardSettings.includePhoto}
                          onChange={(e) => handleSettingsChange('includePhoto', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Include Photo"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cardSettings.includeID}
                          onChange={(e) => handleSettingsChange('includeID', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Include Employee ID"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cardSettings.includePosition}
                          onChange={(e) => handleSettingsChange('includePosition', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Include Position"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cardSettings.includeDepartment}
                          onChange={(e) => handleSettingsChange('includeDepartment', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Include Department"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cardSettings.includeCompany}
                          onChange={(e) => handleSettingsChange('includeCompany', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Include Company"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cardSettings.includeIssueDates}
                          onChange={(e) => handleSettingsChange('includeIssueDates', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Include Issue Dates"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cardSettings.includeQR}
                          onChange={(e) => handleSettingsChange('includeQR', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Include QR Code"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cardSettings.includeContactInfo}
                          onChange={(e) => handleSettingsChange('includeContactInfo', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Include Contact Info"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cardSettings.includeEmergencyInfo}
                          onChange={(e) => handleSettingsChange('includeEmergencyInfo', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Include Emergency Info"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cardSettings.includeBloodGroupFront}
                          onChange={(e) => handleSettingsChange('includeBloodGroupFront', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Include Blood Group (Front)"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cardSettings.includeBloodGroupBack}
                          onChange={(e) => handleSettingsChange('includeBloodGroupBack', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Include Blood Group (Back)"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cardSettings.showBorder}
                          onChange={(e) => handleSettingsChange('showBorder', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Show Border"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cardSettings.roundedCorners}
                          onChange={(e) => handleSettingsChange('roundedCorners', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Rounded Corners"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cardSettings.showShadow}
                          onChange={(e) => handleSettingsChange('showShadow', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Show Shadow"
                    />
                  </FormGroup>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Emergency Contact
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Contact Name"
                        variant="outlined"
                        fullWidth
                        value={cardSettings.emergencyContact.name}
                        onChange={(e) => handleEmergencyContactChange('name', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Relation"
                        variant="outlined"
                        fullWidth
                        value={cardSettings.emergencyContact.relation}
                        onChange={(e) => handleEmergencyContactChange('relation', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Phone"
                        variant="outlined"
                        fullWidth
                        value={cardSettings.emergencyContact.phone}
                        onChange={(e) => handleEmergencyContactChange('phone', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Email"
                        variant="outlined"
                        fullWidth
                        value={cardSettings.emergencyContact.email}
                        onChange={(e) => handleEmergencyContactChange('email', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>

        {idCardExports.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CreditCard size={20} />
              Previous ID Card Exports ({idCardExports.length})
            </Typography>
            <TableContainer component={Paper} elevation={3}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>File Name</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {idCardExports.map((exportData) => (
                    <TableRow key={exportData.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {exportData.user_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {exportData.user_email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {exportData.filename}
                          <Chip size="small" label="Metadata" color="info" variant="outlined" />
                        </Box>
                      </TableCell>
                      <TableCell>{(exportData.file_size / 1024).toFixed(2)} KB</TableCell>
                      <TableCell>
                        {new Date(exportData.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Regenerate and Download PDF">
                            <IconButton
                              size="small"
                              onClick={() => handleRegenerateAndDownload(exportData)}
                              disabled={regenerating[exportData.id]}
                              color="primary"
                            >
                              {regenerating[exportData.id] ? (
                                <CircularProgress size={16} />
                              ) : (
                                <RotateCcw size={16} />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Export">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteExport(exportData.id)}
                              color="error"
                            >
                              <X size={16} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleGeneratePDFs}
          disabled={generating || selectedUsers.size === 0}
          startIcon={generating ? <CircularProgress size={20} /> : <Download />}
        >
          {generating ? 'Generating...' : `Generate ${selectedUsers.size} ID Cards`}
        </Button>
      </DialogActions>

      {/* Standard CSV Upload Dialog */}
      {showCSVUpload && (
        <CSVUploadIDGenerator
          open={showCSVUpload}
          onClose={() => setShowCSVUpload(false)}
          onDataReady={handleCSVData}
        />
      )}

      {/* Multi-Domain CSV Upload Dialog */}
      {showMultiDomainCSV && (
        <MultiDomainCSVUploader
          open={showMultiDomainCSV}
          onClose={() => setShowMultiDomainCSV(false)}
          onDataReady={handleCSVData}
        />
      )}
    </Dialog>
  );
});

export default BulkIDCardGenerator;